import H6 from '@base/H6/H6';
import P2 from '@base/P2/P2';
import { eventHeightMap } from '@cont/PlannerV2/planner-constants';
import PlannerCardFrame from '@cont/PlannerV2/PlannerCards/PlannerCardFrame';
import styled from '@emotion/styled';
import { Spacings, Fonts } from '@styles/index';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import SessionDetails from '@cont/OneToOneLiveMentorship/SessionDetails/Session';
import Tags from '@base/Tags/Tags';
import Avatar from '@base/Avatar/Avatar';
import { LMPSessionStatus } from '@constants/index';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';
import useLmpStore from '@stores/planner/lmpStore';
import { getLMPCancelledSession } from '@stores/selectors/planner';
import useTopology from '@api/hooks/topology/useTopology';
import useSessionStore from '@stores/live-mentorship/sessionStore';
import { PlannerItemWrapper } from '../PlannerElements';

const getBackgroundColor = ($isDarkModeOn, $isCompleted, $isCancelled) => {
  if ($isDarkModeOn || $isCompleted || $isCancelled)
    return 'var(--color-base-1)';
  return 'var(--color-i-lmp)';
};

const ContentWrapper = styled.div`
  background-position: right;
  background-repeat: no-repeat;
  color: ${({ $isCompleted }) =>
    $isCompleted ? 'var(--color-base-fill)' : 'var(--color-i-white)'};
  background-color: ${({ $isDarkModeOn, $isCompleted, $isCancelled }) =>
    getBackgroundColor($isDarkModeOn, $isCompleted, $isCancelled)};
  box-shadow: ${boxShadowGenerator()};
  transition: box-shadow 200ms ease-in-out;
  opacity: ${({ $notAccessible }) => ($notAccessible ? 0.5 : 1)};
  border-radius: ${Spacings.SPACING_2B};
  cursor: ${({ $isUpcoming }) => ($isUpcoming ? 'auto' : 'pointer')};
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_4B};
  font-style: ${Fonts.REGULAR};
  font-weight: 600;
  font-size: 14px;
  line-height: 150%;
  position: relative;
  &:hover {
    box-shadow: ${boxShadowGenerator({
      yOffset: Spacings.SPACING_1B,
      blur: Spacings.SPACING_2B
    })};
  }
`;

const Info = styled(P2)`
  display: flex;
  color: ${({ $isDarkModeOn, $isCompleted, $isCancelled }) =>
    $isCompleted || $isDarkModeOn || $isCancelled
      ? 'var(--color-text-secondary)'
      : 'var(--color-i-white)'};
  align-items: center;
`;

const SessionName = styled(H6)`
  font-size: ${Spacings.SPACING_3B};
  font-weight: ${Fonts.BOLD};
  color: ${({ $isCompleted }) =>
    $isCompleted ? 'var(--color-i-lmp)' : 'var(--color-i-white)'};
  ${({ $isCancelled }) =>
    $isCancelled &&
    `
    color: var(--color-text-secondary)
  `}
`;

const getColor = ($isDarkModeOn, $isCompleted, $isCancelled) => {
  if ($isCancelled) return 'var(--color-text-secondary)';
  if ($isDarkModeOn) return 'var(--color-text-primary)';
  if ($isCompleted) return 'var(--color-base-fill)';
  return 'var(--color-i-white)';
};

const SessionType = styled(H6)`
  font-size: ${Spacings.SPACING_4B};
  margin-top: ${Spacings.SPACING_1B};
  max-height: 18px;
  max-width: 256px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: clip;
  line-height: ${Spacings.SPACING_5B};

  color: ${({ $isDarkModeOn, $isCompleted, $isCancelled }) =>
    getColor($isDarkModeOn, $isCompleted, $isCancelled)};
  ${({ $isCancelled }) =>
    $isCancelled &&
    `
text-decoration: line-through;
color: var(--color-text-secondary)
`}
`;

const LiveTag = styled(Tags)`
  margin-right: 6px;
  height: 16px;
  font-size: ${Fonts.EXTRA_SMALL};
  line-height: 12px;
  font-weight: ${Fonts.BOLD};
  padding: 2px 6px;
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: ${Spacings.SPACING_2B};
`;

const StyledAvatar = styled(Avatar)`
  position: absolute;
  left: 296px;
  top: 22px;
`;

const LMPCard = ({ metaItem }) => {
  const {
    activityAt,
    eventType,
    info,
    educator,
    lmpStatus,
    subjectName,
    isLive,
    uid,
    isCompleted,
    isWelcomeSession
  } = metaItem || {};

  const goalUID = useSelectedGoalUID();
  const goalInfo = useTopology(goalUID);
  const { isK12 } = goalInfo;
  const sessionName = isK12 ? '1:1 live homework help' : '1:1 live mentorship';
  const { isCancelled, isRefunded } = useLmpStore(
    getLMPCancelledSession(goalUID, uid)
  );
  const { sessionDrawerUID: sessionUID, setSessionDrawerUID } =
    useSessionStore();

  const isDarkModeOn =
    useDarkModeStatus(getIsDarkModeOn) && lmpStatus > LMPSessionStatus.STARTED;

  const isLiveLMP = isLive && !isCompleted;

  const getWelcomeSessionInfo = () => {
    if (isCancelled) {
      return isRefunded ? 'Refunded' : 'Cancelled';
    }
    if (lmpStatus <= LMPSessionStatus.STARTED) {
      return info;
    }
    if (
      lmpStatus === LMPSessionStatus.STOPPED ||
      lmpStatus === LMPSessionStatus.RECORDING_AVAILABLE
    ) {
      return 'Completed';
    }
  };

  const getLmpInfo = () => {
    if (isCancelled) {
      return isRefunded
        ? `${subjectName} • Refunded`
        : `${subjectName} • Cancelled`;
    }
    if (lmpStatus <= LMPSessionStatus.STARTED) {
      return `${subjectName} • ${info} `;
    }
    if (
      lmpStatus === LMPSessionStatus.STOPPED ||
      lmpStatus === LMPSessionStatus.RECORDING_AVAILABLE
    ) {
      return `${subjectName} • Completed`;
    }
  };

  const onSessionClick = () => {
    setSessionDrawerUID(uid);
  };

  const getAvatarSrc = () => {
    const pathLastName = isCancelled
      ? 'plannerLMPCancelledEducator.svg'
      : 'lmpPlannerEducator.svg';
    return `/static/images/oneToOneLiveMentoring/${pathLastName}`;
  };

  const itemName = () => {
    return isWelcomeSession ? 'Welcome Session' : metaItem.name;
  };

  return (
    <>
      <PlannerItemWrapper
        $height={eventHeightMap[eventType]}
        onClick={onSessionClick}
      >
        <PlannerCardFrame activityAt={activityAt}>
          <ContentWrapper
            $isCompleted={isCompleted}
            $isDarkModeOn={isDarkModeOn}
            $isCancelled={isCancelled}
          >
            <SessionName $isCompleted={isCompleted} $isCancelled={isCancelled}>
              {sessionName}
            </SessionName>
            <SessionType
              $isCompleted={isCompleted}
              $isDarkModeOn={isDarkModeOn}
              $isCancelled={isCancelled}
            >
              {`${itemName()} ${
                educator ? `• ${educator?.firstName} ${educator.lastName}` : ''
              } `}
            </SessionType>
            <InfoWrapper>
              {isLiveLMP && <LiveTag type="live">Live</LiveTag>}
              <Info
                $isCompleted={isCompleted}
                $isDarkModeOn={isDarkModeOn}
                $isCancelled={isCancelled}
              >
                {isWelcomeSession ? getWelcomeSessionInfo() : getLmpInfo()}
              </Info>
            </InfoWrapper>
            <StyledAvatar
              $isEducator={educator?.avatar}
              $isCancelled={isCancelled && !educator}
              src={educator ? educator?.avatar : getAvatarSrc()}
              alt="educator-icon"
              size={Spacings.SPACING_12B}
              circular
              onClick={() => {}}
            />
          </ContentWrapper>
        </PlannerCardFrame>
      </PlannerItemWrapper>
      {sessionUID === uid && <SessionDetails goalUID={goalUID} />}
    </>
  );
};

export default LMPCard;
